import React from "react";
import { useFormContext } from "react-hook-form";
import { MFAFlowAction } from "common-client/utils/useMFAFlow";
import {
  UserLoginErrorCode,
  useSendMfaCodeMutation,
  useVerifyMfaCodeMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { ProvideCode } from "./CommonLoginComponents/ProvideCode";
import { LoginFormStructure } from "./LoginPage";

export type ProvidePhoneCodeProps = {
  allowAnotherNumber: boolean;
  allowResetRequest?: boolean;
  dispatch: React.Dispatch<MFAFlowAction>;
  error: Maybe<{ message: string; code?: UserLoginErrorCode }>;
  maskedPhoneNumber: string;
  onSuccess: () => void;
};

export const ProvidePhoneCode = ({
  allowAnotherNumber,
  allowResetRequest = false,
  dispatch,
  error,
  maskedPhoneNumber,
  onSuccess,
}: ProvidePhoneCodeProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const { handleSubmit, setValue } = useFormContext();
  const [isSendCodeThrottled, setIsSendCodeThrottled] = React.useState(false);
  const [retryTimeout, setRetryTimeout] = React.useState(0);

  React.useEffect(() => {
    setIsSendCodeThrottled(true);
    const timeoutId = setTimeout(() => {
      setIsSendCodeThrottled(false);
      setRetryTimeout(0);
    }, retryTimeout * 1000);
    return () => clearTimeout(timeoutId);
  }, [retryTimeout]);

  const [sendCode] = useSendMfaCodeMutation({
    onCompleted: data => {
      if (!data.sendMFACode.error) {
        addSuccessToast("Authentication code has been re-sent.");
      }
      if (data.sendMFACode.retryTimeout) {
        setRetryTimeout(data.sendMFACode.retryTimeout);
      }
      setValue("code", "");
      dispatch({ type: "sendCode", data: data.sendMFACode });
    },
    onError: () => {
      addErrorToast(
        `There was an issue sending the code. Please try again. If the problem persists, please email us at support@withforerunner.com`,
      );
    },
  });

  const [verifyCode] = useVerifyMfaCodeMutation({
    onCompleted: data => {
      if (data.verifyMFACode.ok) {
        onSuccess();
      } else {
        dispatch({ type: "verifyCode", data: data.verifyMFACode });
      }
    },
    onError: () => {
      addErrorToast(
        `There was an issue verifying the code. Please try again. If the problem persists, please email us at support@withforerunner.com`,
      );
    },
  });

  const handleLogin = async (loginData: LoginFormStructure) => {
    await verifyCode({
      variables: {
        data: {
          email: loginData.email,
          password: loginData.password,
          phoneNumber: loginData.phoneNumber,
          code: loginData.code!,
        },
      },
    });
  };

  const handleResend = async (loginData: LoginFormStructure) => {
    if (retryTimeout === 0) {
      setIsSendCodeThrottled(true);
      await sendCode({
        variables: {
          data: {
            email: loginData.email,
            password: loginData.password,
            phoneNumber: loginData.phoneNumber!,
            isRetry: true,
          },
        },
      });
    }
  };

  const useAnotherPhoneNumber = () => {
    setValue("code", "");
    dispatch({ type: "anotherNumber" });
  };

  const requestReset = () => {
    dispatch({ type: "initiateResetRequest" });
  };

  const secondaryActions: {
    onClick: () => void;
    text: string;
    disabled?: boolean;
  }[] = [
    {
      onClick: handleSubmit(handleResend),
      text: "Resend authentication code",
      disabled: isSendCodeThrottled,
    },
  ];

  if (allowAnotherNumber) {
    secondaryActions.push({
      onClick: useAnotherPhoneNumber,
      text: "Use another phone number",
    });
  }

  if (allowResetRequest) {
    secondaryActions.push({
      onClick: requestReset,
      text: "Locked out? Request to reset your Multi-Factor Authentication",
    });
  }

  return (
    <ProvideCode
      title="Enter authentication code"
      subtitle={`Please enter the authentication code we sent to your mobile phone number ending in ${maskedPhoneNumber}.`}
      secondaryActions={secondaryActions}
      onSubmit={handleLogin}
      error={error}
    />
  );
};
